import { type AxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  CustomerCredentials,
  isFullCredentials,
} from "../ui/customerDataStore";
import { captureException } from "@sentry/react";
import type {
  CustomerFundSwitch,
  CustomerFundSwitchAllocation,
  InsuranceManagementDetail,
} from "../../types";

// ALL makes the calculation reallocate, and advise on, the insurances that are not selected for
// transfer as well; without it the response carries no fund switches. The advisor tool calculates
// under the same scope, so the customer sees the advice the advisor saw.
const CALCULATION_SCOPE = "ALL";

const getCalculationById = async ({
  credentials,
  responseType = "CUSTOMER_ADVICE",
}: {
  credentials: CustomerCredentials;
  responseType?: string;
}) => {
  if (!credentials?.identityId) {
    throw new Error("Invalid credentials: identityId is required");
  }

  const signedRequest = await signApiRequest(
    "GET",
    `${import.meta.env.VITE_API_URL}/calculation/calculation?responseType=${responseType}&fid=${credentials.identityId}&scope=${CALCULATION_SCOPE}`,
    credentials,
    { "content-type": "application/json" },
  );

  const response = await apiClient(signedRequest);
  return response.data;
};

export const useGetCalculationById = ({
  credentials,
  enabled = true,
  responseType = "CUSTOMER_ADVICE",
  refetchInterval = false,
}: {
  credentials: CustomerCredentials;
  enabled?: boolean;
  responseType?: string;
  /** Pension data arrives over hours or days, so the app polls while it is collecting. */
  refetchInterval?: number | false;
}) => {
  const hasValidCredentials = enabled && isFullCredentials(credentials);

  const creditScoresQuery = useQuery<TCalulcationResponse, AxiosError>({
    queryKey: ["get-calculation-by-id", responseType],
    queryFn: () =>
      getCalculationById({
        credentials,
        responseType,
      }),
    enabled: hasValidCredentials,
    refetchInterval,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-calculation-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: `/calculation/calculation?responseType=${responseType}&fid=${credentials.identityId}`,
        },
        level: "error",
      });
      return true;
    },
  });
  return creditScoresQuery;
};

export const getCalculationQueryOptions = (
  credentials: CustomerCredentials,
  responseType?: string,
) =>
  queryOptions({
    queryKey: ["get-calculation-by-id", responseType],
    queryFn: () => getCalculationById({ credentials, responseType }),
    retry: false,
    staleTime: 30_000,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-calculation-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint:
            "/calculation/calculation?responseType=CUSTOMER_ADVICE&fid=${identityId}",
        },
        level: "error",
      });
      return true;
    },
  });

export enum TRANSFER_STATUSES {
  AWAIT_SIGN_DIGITAL = "AWAIT_SIGN_DIGITAL",
  AWAIT_SIGN_POST = "AWAIT_SIGN_POST",
  AWAIT_SIGN_EMPLOYER = "AWAIT_SIGN_EMPLOYER",
  TRANSFER_ONGOING = "TRANSFER_ONGOING",
  NEED_COMPLETION = "NEED_COMPLETION",
  TRANSFER_ABORTED = "TRANSFER_ABORTED",
  TRANSFER_COMPLETE = "TRANSFER_COMPLETE",
  MONITORED = "MONITORED",
  NEW = "NEW",
}

export interface Insurance {
  insuranceIdentifier: string;
  insuranceCapital: string;
  insuranceCompany: string;
  // Only the APP_OVERVIEW response carries it so far.
  insuranceCompanyOrgNr?: string | null;
  owner: {
    legalName: string;
  };
  logotype: string;
  transferStatus?: TRANSFER_STATUSES;
  employer?: string;
  ownManagement?: boolean;
  transferDocument?: string;
  statusInternal?: TRANSFER_STATUSES;
  isDigitalTransfer?: boolean;
  // When the pension data behind this holding was last refreshed, shown to the
  // customer as "Uppgifter från". Null for holdings that carry no import date.
  updatedAt?: string | null;
  managementDetails?: InsuranceManagementDetail[]; // The capital per management type; a combined policy has one entry per part.
}

export interface PaymentDistribution {
  T_DC: number;
  T_NonDC: number;
  K: number;
  P: number;
  G: number;
  PPM: number | null;
  I: number | null;
  T_DC_Percentage: number | null;
  T_NonDC_Percentage: number | null;
  K_Percentage: number | null;
  P_Percentage: number;
  G_Percentage: number | null;
  PPM_Percentage: number | null;
  I_Percentage: number | null;
  firstMonthPension: number | null;
  totalPension: number | null;
  guaranteePension: number | null;
  affectedByRisk: number | null;
}

export interface AssetBreakdown {
  buildingblockName: string;
  buildingblock: string;
  value: number;
  percentage: number;
}

export interface TotalAllocation {
  assetAllocationForTransfer: {
    eq: number;
    ir: number;
    alt: number;
  };
  assetAllocationTotalBreakdown: AssetBreakdown[];
  sumOfAssets: number;
  sumOfInsuranceAssets: number;
}

export interface TransferAsset {
  name: string;
  percentage: number;
  isin?: string;
}

export interface InsuranceForTransfer {
  insuranceIdentifier: string;
  insuranceCapital: string;
  insuranceCompany: string;
  owner: {
    legalName: string;
  };
  logotype: string;
  employer?: string;
  transferDocument?: string;
  statusInternal: TRANSFER_STATUSES;
  isDigitalTransfer: boolean;
  ownManagement?: boolean;
}

export interface InsurancesForTransfer {
  insuranceTransferList: InsuranceForTransfer[];
  totalTransferableCapital: number;
  capitalForTransfer: number;
  precentageOfTotal: number;
}

export interface TransferAllocation {
  assetAllocation: TransferAsset[];
  insurancesForTransfer: InsurancesForTransfer;
}

export interface PensionAmount {
  totalPension: number;
  statePensionTotal: number;
}

export interface AdviceSummary {
  advicedPensionAmount: PensionAmount;
  currentPensionAmount: PensionAmount;
  totalCurrentFees: number;
  totalAdvicedFees: number;
  pensionDifference: number;
  feeDifference: number;
}

export interface SummaryTable {
  expectedPension: number;
  expectedHighYieldPension: number;
  expectedLowYieldPension: number;
  advisedExpectedPension: number;
  advisedHighYieldPension: number;
  advisedLowYieldPension: number;
  diffExpectedPension: number;
  diffHighYieldPension: number;
  diffLowYieldPension: number;
}

export interface TCalulcationResponse {
  parameters: TGetCalculationParams;
  totalInsuranceList: Insurance[];
  paymentDistribution: PaymentDistribution;
  totalAllocation: TotalAllocation;
  transferAllocation: TransferAllocation | null;
  adviceSummary?: AdviceSummary;
  summaryTable?: SummaryTable;
  managedAllocation?: ManagedAllocation;
  // Only the APP_OVERVIEW response type carries this block.
  forecast?: AppOverviewForecast;
  fundSwitches?: CustomerFundSwitch[];
  fundSwitchAllocation?: CustomerFundSwitchAllocation;
}

export interface AppOverviewForecast {
  // ACTIVE_PREMIUM means the forecast uses the premiums actually paid into the
  // customer's active policies; FALLBACK means a schablon was used instead.
  basis: "ACTIVE_PREMIUM" | "FALLBACK" | "UNAVAILABLE";
  reason: string;
  missingIncome: boolean;
  expectedFeesUntilRetirement: number | null;
}

export interface ManagedAllocation {
  assetAllocation: AssetAllocation[];
  totalAssetValue: number;
  firstMonthPension: number;
}

export interface AssetAllocation {
  buildingblockName: string;
  buildingblock: string;
  value: number;
  percentage: number;
  isin?: string;
  name?: string;
}

export type TGetCalculationParams = {
  zAge: number;
  paymentPeriod: number;
  paymentProtectionPeriod: number;
  crashScenario: number;
  riskPreference: number;
  statePensionAge: number;
};
